import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  Landmark, RefreshCw, ShieldCheck, FlaskConical, Pencil, Check, X, Clock, Link2, Unplug,
} from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "never";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}

function Meta({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm font-medium tabular-nums">{value}</div>
    </div>
  );
}

export default function AccountHeader({ account, onRename, onSync, syncing, onShare, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(account?.name || "");
  const [saving, setSaving] = useState(false);

  if (!account) return null;

  const isReal = account.account_type === "real";
  const platform = (account.platform || "").toUpperCase();

  const saveName = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === account.name) { setEditing(false); return; }
    setSaving(true);
    try {
      await onRename(trimmed);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border bg-card p-4 sm:p-5" data-testid="account-header">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
            <Landmark className="h-5 w-5 text-primary" />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {editing ? (
                <div className="flex items-center gap-1.5">
                  <Input value={name} autoFocus className="h-8 w-56 text-base font-semibold"
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") { setEditing(false); setName(account.name); } }}
                    data-testid="account-name-input" />
                  <Button size="icon" className="h-7 w-7" onClick={saveName} disabled={saving} data-testid="account-name-save">
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(false); setName(account.name); }}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <>
                  <h1 className="font-display text-xl font-semibold tracking-tight sm:text-2xl" data-testid="account-header-name">
                    {account.name}
                  </h1>
                  {!account.is_demo && (
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground"
                      onClick={() => { setName(account.name); setEditing(true); }} data-testid="account-name-edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <Badge variant="outline" className="text-[10px]">{platform || "MT"}</Badge>
              <Badge
                variant="outline"
                className={cn("gap-1 text-[10px]",
                  isReal
                    ? "border-[hsl(var(--profit))]/40 text-[hsl(var(--profit))]"
                    : "border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))]")}
                data-testid="account-type-badge">
                {isReal ? <ShieldCheck className="h-3 w-3" /> : <FlaskConical className="h-3 w-3" />}
                {isReal ? "REAL" : "DEMO"}
              </Badge>
              {account.is_demo && <Badge variant="secondary" className="text-[10px]">Sample data</Badge>}
              <span className="text-xs text-muted-foreground">{account.broker || account.server || "\u2014"}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {!account.is_demo && (
            <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <Clock className="h-3 w-3" /> Last synced {timeAgo(account.last_synced_at)}
            </span>
          )}
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs"
            onClick={onShare} data-testid="account-header-share">
            <Link2 className="h-3.5 w-3.5" /> Share
          </Button>
          {!account.is_demo && (
            <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs"
              onClick={onSync} disabled={syncing} data-testid="account-header-sync">
              <RefreshCw className={cn("h-3.5 w-3.5", syncing && "animate-spin")} /> Sync now
            </Button>
          )}
          {!account.is_demo && onDelete && (
            <Button variant="outline" size="sm"
              className="h-8 gap-1.5 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={onDelete} data-testid="account-header-delete">
              <Unplug className="h-3.5 w-3.5" /> Disconnect
            </Button>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-x-8 gap-y-3 border-t pt-4 sm:grid-cols-4 lg:grid-cols-6">
        <Meta label="Broker" value={account.broker} />
        <Meta label="Server" value={account.server} />
        <Meta label="Login" value={account.login} />
        <Meta label="Currency" value={account.currency} />
        <Meta label="Leverage" value={account.leverage ? `1:${account.leverage}` : null} />
        <Meta label="Type" value={isReal ? "Real" : "Demo"} />
      </div>

      {account.strategy && (
        <p className="mt-3 text-xs text-muted-foreground/90">{account.strategy}</p>
      )}
    </div>
  );
}
